// eslint-disable-next-line no-restricted-imports
import { useTheme } from '@emotion/react';
// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { effects } from '@iheartjane/reefer';
import { useRef } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import type { CSSTransitionProps } from 'react-transition-group/CSSTransition';

import { useCustomerSelector } from '@jane/monolith/customer/redux';
import { selectNotifications } from '@jane/monolith/customer/redux/notifications';
import exclamationIcon from '@jane/shared/assets/icons/exclamation.svg';

import { useHideNotifications } from './useHideNotifications';

interface NotificationContainerProps {
  notificationColor: string;
}

const NotificationContainer = styled.div<NotificationContainerProps>(
  ({ theme }) => ({
    display: 'flex',
    position: 'fixed',
    top: '15px',
    right: '25px',
    width: '240px',
    transition: 'all 0.5s ease-in-out',
    borderRadius: '6px',
    fontSize: '13px',
    lineHeight: '1.3m',
    boxShadow: `0 1px 5px 0 ${effects.lighten('#000', 0.5)}`,
    zIndex: 'var(--layers-toast)',
    color: theme.colors.grays.white,
  }),
  ({ notificationColor }) => ({
    backgroundColor: notificationColor,
  })
);

const NotificationInner = styled.div({
  display: 'flex',
  alignItems: 'center',
  padding: '11px 10px',
});

const NotificationsExclamation = styled.div({
  width: '36px',
  height: '36px',
  marginRight: '12px',
  backgroundImage: `url("${exclamationIcon}")`,
});

const NotificationsText = styled.div({
  flex: 1,
});

/** The props `TransitionGroup` clones onto each of its direct children. */
type InjectedTransitionProps = Pick<
  CSSTransitionProps<HTMLDivElement>,
  'appear' | 'enter' | 'exit' | 'in' | 'onExited'
>;

interface ServerNotificationProps extends InjectedTransitionProps {
  message: string;
  notificationColor: string;
}

const ServerNotification = ({
  message,
  notificationColor,
  ...transitionProps
}: ServerNotificationProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <CSSTransition
      {...transitionProps}
      classNames="notifications"
      nodeRef={nodeRef}
      timeout={500}
    >
      <NotificationContainer
        ref={nodeRef}
        aria-live="polite"
        notificationColor={notificationColor}
      >
        <NotificationInner>
          <NotificationsExclamation />
          <NotificationsText>{message}</NotificationsText>
        </NotificationInner>
      </NotificationContainer>
    </CSSTransition>
  );
};

export const ServerNotifications = () => {
  const hideNotifications = useHideNotifications();
  const theme = useTheme();
  const {
    notificationOptions: { type, messages },
    notificationShown,
  } = useCustomerSelector(selectNotifications);

  const notificationColor =
    type === 'error'
      ? theme.colors.system.negative.main
      : theme.colors.system.positive.main;

  if (!notificationShown || !messages || hideNotifications) return null;

  return (
    <TransitionGroup>
      {messages.map((message, index) => (
        <ServerNotification
          key={index}
          message={message}
          notificationColor={notificationColor}
        />
      ))}
    </TransitionGroup>
  );
};
